import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Card, CardContent } from "../../ui/card";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Skeleton } from "../../ui/skeleton";
import { Tabs, TabsContent } from "../../ui/tabs";
import Pagenation from "../Layout/Pagenation";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table";
import {  ClipboardList, Calendar, DollarSign, Users, ExternalLink } from "lucide-react";
import { format } from "date-fns";
import { cn } from "../../lib/utils";

const statusColors: Record<string, string> = {
  enquiry: "bg-muted text-muted-foreground",
  pending_payment: "bg-yellow-100 text-yellow-800",
  paid: "bg-blue-100 text-blue-800",
  confirmed: "bg-green-100 text-green-800",
  in_progress: "bg-purple-100 text-purple-800",
  completed: "bg-accent/20 text-accent-foreground",
  cancelled: "bg-destructive/20 text-destructive",
};

const statusLabels: Record<string, string> = {
  enquiry: "Enquiry",
  pending_payment: "Pending Payment",
  paid: "Paid",
  confirmed: "Confirmed",
  in_progress: "In Progress",
  completed: "Completed",
  cancelled: "Cancelled",
};
interface  orderProds  {
  orders: any ;
};

interface orderProds {
  orders: any;
  loading:boolean;
  totalRecords:number;
  totalPage:number;
  currentPage:number;
  sateValChange?: (field: string, value: string) => void;
  error:boolean;
  
}


const AdminCatering = ({ orders, loading, totalRecords, totalPage, currentPage, sateValChange, error }: orderProds) => {
  const location = useLocation();
  const [statusFilter, setStatusFilter] = useState<string>("all");

  // Stats
  const totalOrders = orders.length;
  const totalRevenue = orders.filter((o:any) => o.orderStatus === "paid" || o.orderStatus === "confirmed" || o.orderStatus === "completed").reduce((sum:number, o:any) => sum + o.orderGrandTotal, 0);
  const pendingEnquiries = orders.filter((o:any) => o.orderStatus === "enquiry").length;
  const upcomingEvents = orders.filter((o:any) => new Date(o.orderDate) >= new Date() && (o.orderStatus === "paid" || o.orderStatus === "confirmed")).length;


  const currentTab =
    location.pathname === "/admin/catering/menus" ? "menus" : location.pathname === "/admin/catering/settings" ? "settings" : location.pathname === "/admin/catering/addons" ? "addons" : "orders";

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <main className="flex-1 py-8">
        <div className="container">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold">Catering Orders</h1>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-accent/10">
                    <ClipboardList className="h-5 w-5 text-accent" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Orders</p>
                    <p className="text-2xl font-bold">{totalRecords}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-accent/10">
                    <DollarSign className="h-5 w-5 text-accent" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Revenue</p>
                    <p className="text-2xl font-bold">${totalRevenue.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-accent/10">
                    <Users className="h-5 w-5 text-accent" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Enquiries</p>
                    <p className="text-2xl font-bold">{pendingEnquiries}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-accent/10">
                    <Calendar className="h-5 w-5 text-accent" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Upcoming</p>
                    <p className="text-2xl font-bold">{upcomingEvents}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tabs Navigation */}
          <Tabs value={currentTab} className="space-y-6">
            <TabsContent value="orders" className="space-y-6">
              {/* Filters */}
              <div className="flex items-center gap-4">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="enquiry">Enquiries</SelectItem>
                    <SelectItem value="pending_payment">Pending Payment</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Orders Table */}
              <Card>
                <CardContent className="p-0">
                  {loading ? (
                    <div className="p-8 space-y-4">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Skeleton key={i} className="h-12 w-full" />
                      ))}
                    </div>
                  ) : error ? (
                    <div className="p-8 text-center text-muted-foreground">Failed to load orders</div>
                  ) : orders.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground">No orders found</div>
                  ) : (
                    <>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Order #</TableHead>
                            <TableHead>Customer</TableHead>
                            <TableHead>Menu</TableHead>
                            <TableHead>Event Date</TableHead>
                            <TableHead>Pax</TableHead>
                            <TableHead>Total</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {orders.map((order) => (
                            <TableRow key={order._id}>
                              <TableCell className="font-mono text-sm">{order.orderNumber}</TableCell>
                              <TableCell>
                                <div>
                                  <p className="font-medium">{order.ordercustomerName}</p>
                                  <p className="text-sm text-muted-foreground">{order.orderCustomerEmail}</p>
                                </div>
                              </TableCell>
                              <TableCell>{order.orderProductName}</TableCell>
                              <TableCell>
                                {format(new Date(order.orderDate), "MMM d, yyyy")}
                                <br />
                                <span className="text-sm text-muted-foreground">{order.orderTime}</span>
                              </TableCell>
                              <TableCell>{order.orderPax}</TableCell>
                              <TableCell className="font-semibold">${order.orderGrandTotal.toFixed(2)}</TableCell>
                              <TableCell>
                                <Badge className={cn("capitalize", statusColors[order.orderStatus])}>{statusLabels[order.orderStatus] || order.orderStatus}</Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                <Link to={`/clientpanel/catering/orders/${order._id}`}>
                                  <Button variant="ghost" size="sm">
                                    View
                                    <ExternalLink className="h-4 w-4 ml-1" />
                                  </Button>
                                </Link>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      <Pagenation
                        params={{
                          totalRecords,
                          totalPage,
                          currentPage,
                        }}
                        sateValChange={sateValChange}
                      />
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}

export default AdminCatering;